"use client";

import { useId, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { Modal } from "./Modal";
import { addLine } from "@/lib/cart-store";
import { productToItem, trackAddToCart } from "@/lib/analytics";
import { fireCartToast } from "./CartToast";
import type { Product } from "@/app/data/products";
import { formatPrice } from "@/app/data/products";
import { useT } from "./LocaleProvider";

/**
 * ProductActions — the hover/tap quick-add + quick-view layer that overlays
 * a ProductCard. Designed to sit INSIDE the card's relative container, with
 * the card's PDP <Link> rendered separately underneath at z-0.
 *
 * Desktop: two buttons revealed on group-hover (QUICK ADD + PREVIEW),
 *   plus keyboard focus reveals them too (group-focus-within).
 * Mobile: a single compact "+" pill always visible in the top-right corner.
 *   Tap → opens size picker modal. PDP navigation stays on the main card link.
 *
 * Accessibility:
 *  - Buttons have full aria-labels naming the product
 *  - Size picker uses fieldset/legend + radio semantics
 *  - Modals are native <dialog> (focus trap + Esc-to-close are free)
 *  - aria-live toast announces the addition without stealing focus
 */
export function ProductActions({ product }: { product: Product }) {
  const soldOut = product.sizesAvailable.length === 0;
  const [addOpen, setAddOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const t = useT();

  const productLabel = `${product.brand} ${product.model} — ${product.colorway}`;

  if (soldOut) return null;

  return (
    <>
      {/* ─── Desktop: hover-reveal overlay ─────────────────────── */}
      <div
        className="pointer-events-none absolute inset-0 z-10 hidden items-center justify-center gap-2 p-3 opacity-0 transition-opacity duration-200 group-hover:opacity-100 group-focus-within:opacity-100 md:flex"
        aria-hidden={!addOpen && !viewOpen ? undefined : "true"}
      >
        <div className="pointer-events-auto absolute inset-0 bg-gradient-to-t from-[var(--color-ink)]/65 via-[var(--color-ink)]/30 to-transparent" />
        <div className="pointer-events-auto relative flex flex-col gap-1.5">
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setAddOpen(true);
            }}
            aria-label={`Quick add ${productLabel} to bag`}
            className="mono-strip rounded-sm border border-[var(--color-bone)] bg-[var(--color-bone)] px-5 py-2.5 text-[10px] text-[var(--color-ink)] shadow-lg transition hover:bg-[var(--color-blood)] hover:text-[var(--color-bone)] hover:border-[var(--color-blood)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-foil)]"
          >
            {t({ en: "Quick add", es: "Agregar" })}
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setViewOpen(true);
            }}
            aria-label={`Preview ${productLabel}`}
            className="mono-strip inline-flex items-center justify-center gap-1.5 rounded-sm border border-[var(--color-bone)]/50 bg-transparent px-5 py-2 text-[10px] text-[var(--color-bone)] backdrop-blur transition hover:border-[var(--color-foil)] hover:text-[var(--color-foil)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-foil)]"
          >
            <EyeIcon className="h-3 w-3" />
            Preview
          </button>
        </div>
      </div>

      {/* ─── Mobile: corner pill, always visible. 44x44 WCAG tap target. ─── */}
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setAddOpen(true);
        }}
        aria-label={`Quick add ${productLabel} to bag`}
        className="absolute right-2 top-2 z-10 inline-flex h-11 min-w-11 items-center justify-center gap-1 rounded-full border border-[var(--color-ink)]/10 bg-[var(--color-bone)]/95 px-3 font-mono text-[10px] uppercase tracking-[0.14em] text-[var(--color-ink)] shadow-md backdrop-blur-sm transition active:scale-95 md:hidden"
      >
        <PlusIcon className="h-3.5 w-3.5" />
        <span>Add</span>
      </button>

      {/* ─── Modals ────────────────────────────────────────────── */}
      <SizePickerModal
        product={product}
        open={addOpen}
        onClose={() => setAddOpen(false)}
      />
      <QuickViewModal
        product={product}
        open={viewOpen}
        onClose={() => setViewOpen(false)}
        onOpenAdd={() => {
          setViewOpen(false);
          setAddOpen(true);
        }}
      />
    </>
  );
}

// ─── Size picker modal ──────────────────────────────────────────────
function SizePickerModal({
  product,
  open,
  onClose,
}: {
  product: Product;
  open: boolean;
  onClose: () => void;
}) {
  const titleId = useId();
  const [selected, setSelected] = useState<number | null>(null);
  const productLabel = `${product.brand} ${product.model} — ${product.colorway}`;
  const t = useT();

  function handleAdd() {
    if (selected == null) return;
    addLine({ product, size: selected });
    trackAddToCart(productToItem(product));
    fireCartToast({ productName: productLabel, size: selected });
    setSelected(null);
    onClose();
  }

  return (
    <Modal open={open} onClose={onClose} labelledBy={titleId} size="sm">
      <div className="flex items-start gap-4 p-5">
        <div className="relative h-24 w-24 flex-shrink-0 overflow-hidden bg-white">
          {product.imageSrc && (
            <Image
              src={product.imageSrc}
              alt=""
              fill
              sizes="96px"
              className="object-contain p-2"
            />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="mono-strip text-[10px] text-[var(--color-foil)]">
            {product.brand.toUpperCase()} · {product.sku}
          </div>
          <h2
            id={titleId}
            className="font-display mt-1 text-lg uppercase leading-tight"
          >
            {product.model}
          </h2>
          <div className="font-mono text-xs text-[var(--color-bone-dim)]">
            {product.colorway}
          </div>
          <div className="mt-2 font-mono text-base text-[var(--color-bone)]">
            {formatPrice(product.priceCents)}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close size picker"
          className="-mr-1 -mt-1 inline-flex h-8 w-8 items-center justify-center rounded-full text-[var(--color-bone-dim)] transition hover:bg-[var(--color-bone)]/10 hover:text-[var(--color-bone)]"
        >
          <CloseIcon className="h-4 w-4" />
        </button>
      </div>

      <div className="border-t border-[var(--color-bone)]/10 px-5 py-4">
        <fieldset>
          <legend className="mono-strip mb-3 text-[10px] text-[var(--color-bone-dim)]">
            {t({ en: "Select size · US", es: "Elige talla · US" })}
          </legend>
          <div
            role="radiogroup"
            aria-label="Select your US size"
            className="grid grid-cols-5 gap-1.5"
          >
            {product.sizesAvailable.map((size) => {
              const active = selected === size;
              return (
                <button
                  key={size}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => setSelected(size)}
                  className={[
                    "rounded-sm border px-2 py-2 font-mono text-sm transition",
                    active
                      ? "border-[var(--color-blood)] bg-[var(--color-blood)] text-white"
                      : "border-[var(--color-bone)]/20 text-[var(--color-bone)] hover:border-[var(--color-foil)] hover:text-[var(--color-foil)]",
                    "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-foil)]",
                  ].join(" ")}
                >
                  {size}
                </button>
              );
            })}
          </div>
        </fieldset>
      </div>

      <div className="flex items-center justify-between gap-3 border-t border-[var(--color-bone)]/10 bg-[var(--color-ink)]/60 px-5 py-4">
        <Link
          href={`/shop/${product.id}`}
          className="mono-strip text-[10px] text-[var(--color-bone-dim)] underline-offset-4 transition hover:text-[var(--color-foil)] hover:underline"
        >
          {t({ en: "Full details →", es: "Ver detalles →" })}
        </Link>
        <button
          type="button"
          onClick={handleAdd}
          disabled={selected == null}
          className="mono-strip rounded-sm bg-[var(--color-blood)] px-6 py-2.5 text-[11px] text-white transition hover:bg-[var(--color-blood)]/85 disabled:cursor-not-allowed disabled:opacity-40 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-foil)]"
        >
          {t({ en: "Add to bag", es: "Agregar" })} · {formatPrice(product.priceCents)}
        </button>
      </div>
    </Modal>
  );
}

// ─── Quick view modal ────────────────────────────────────────────────
function QuickViewModal({
  product,
  open,
  onClose,
  onOpenAdd,
}: {
  product: Product;
  open: boolean;
  onClose: () => void;
  onOpenAdd: () => void;
}) {
  const titleId = useId();

  return (
    <Modal open={open} onClose={onClose} labelledBy={titleId} size="lg">
      <div className="grid gap-0 sm:grid-cols-2">
        <div className="relative aspect-square w-full bg-white">
          {product.imageSrc && (
            <Image
              src={product.imageSrc}
              alt={`${product.brand} ${product.model} — ${product.colorway}`}
              fill
              sizes="(min-width: 640px) 384px, 100vw"
              className="object-contain p-6"
            />
          )}
        </div>
        <div className="flex flex-col gap-4 p-6 sm:p-8">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="mono-strip text-[10px] text-[var(--color-foil)]">
                {product.brand.toUpperCase()} · {product.sku}
              </div>
              <h2
                id={titleId}
                className="font-display mt-2 text-2xl uppercase leading-tight"
              >
                {product.model}
              </h2>
              <div className="font-mono text-sm text-[var(--color-bone-dim)]">
                {product.colorway}
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close preview"
              className="-mr-1 -mt-1 inline-flex h-8 w-8 items-center justify-center rounded-full text-[var(--color-bone-dim)] transition hover:bg-[var(--color-bone)]/10 hover:text-[var(--color-bone)]"
            >
              <CloseIcon className="h-4 w-4" />
            </button>
          </div>

          <div className="my-2 border-y border-[var(--color-bone)]/10 py-3">
            <div className="font-mono text-2xl text-[var(--color-bone)]">
              {formatPrice(product.priceCents)}
            </div>
            <div className="mono-strip mt-1 text-[10px] text-[var(--color-bone-dim)]">
              {product.sizesAvailable.length} sizes in stock · US{" "}
              {Math.min(...product.sizesAvailable)}–
              {Math.max(...product.sizesAvailable)}
            </div>
          </div>

          <div className="flex flex-col gap-3">
            <button
              type="button"
              onClick={onOpenAdd}
              className="mono-strip w-full rounded-sm bg-[var(--color-blood)] px-6 py-3 text-[11px] text-white transition hover:bg-[var(--color-blood)]/85 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-foil)]"
            >
              Pick size + add to bag
            </button>
            <Link
              href={`/shop/${product.id}`}
              className="mono-strip w-full rounded-sm border border-[var(--color-bone)]/30 px-6 py-3 text-center text-[11px] text-[var(--color-bone)] transition hover:border-[var(--color-foil)] hover:text-[var(--color-foil)]"
            >
              See full details
            </Link>
          </div>
        </div>
      </div>
    </Modal>
  );
}

// ─── Icons ────────────────────────────────────────────────────────
function PlusIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <path
        d="M12 5v14M5 12h14"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function EyeIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <path
        d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z"
        stroke="currentColor"
        strokeWidth="1.8"
      />
      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  );
}

function CloseIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <path
        d="M6 6l12 12M18 6L6 18"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}
